/**
 * Encart « Parcours » des statistiques en direct : pages d'entrée et d'achat,
 * étape par étape (visiteurs + évolution), le détail des pages de chaque
 * étape au toucher, puis les résultats (démo, comptes créés, ventes).
 */
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { formatMetric } from '@/lib/admin/live-stats';
import {
  rateLabel,
  stepsOf,
  stepVisitors,
  type FunnelStats,
  type FunnelStep,
} from '@/lib/admin/funnel-stats';
import { TrendBadge } from './trend-badge';
import { PageName } from './page-name';

function plural(n: number, one: string, many: string) {
  return `${formatMetric('visitors', n)} ${n > 1 ? many : one}`;
}

function StepRow({ step, max }: { step: FunnelStep; max: number }) {
  const [open, setOpen] = useState(false);
  const hasPages = step.pages.length > 0;
  const app = step.key === 'landing' ? 'site' : 'app';
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={!hasPages}
        aria-expanded={hasPages ? open : undefined}
        className="hover:bg-ink-50 -mx-2 block min-h-[2.75rem] w-[calc(100%+1rem)] rounded-xl px-2 py-1.5 text-left transition active:scale-[0.99] disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className="flex items-center justify-between gap-2">
          <span className="text-ink-800 flex min-w-0 items-center gap-1 text-sm font-semibold">
            {hasPages && (
              <ChevronDown
                className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
                aria-hidden
              />
            )}
            <span className="truncate">{step.label}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            <span className="text-ink-900 font-semibold tabular-nums">
              {formatMetric('visitors', step.visitors)}
            </span>
            <TrendBadge cur={step.visitors} prev={step.prev_visitors} />
          </span>
        </span>
        <span className="bg-ink-50 mt-1 block h-1.5 overflow-hidden rounded-full" aria-hidden>
          <span
            className="bg-primary-500 block h-full rounded-full"
            style={{ width: `${(step.visitors / max) * 100}%` }}
          />
        </span>
        <span className="text-ink-400 mt-0.5 block text-xs">
          {plural(step.visitors, 'visiteur', 'visiteurs')} · {plural(step.views, 'vue', 'vues')}
        </span>
      </button>
      {open && hasPages && (
        <ul className="border-ink-100 mb-2 ml-5 flex flex-col gap-1.5 border-l pl-3">
          {step.pages.map((p) => (
            <li key={p.path} className="flex items-start justify-between gap-2">
              <PageName app={app} path={p.path} label={p.label} />
              <span className="text-ink-600 shrink-0 text-xs tabular-nums">
                {plural(p.visitors, 'visiteur', 'visiteurs')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Result({
  label,
  value,
  cur,
  prev,
  note,
}: {
  label: string;
  value: string;
  cur: number;
  prev: number;
  note?: string | null;
}) {
  return (
    <div>
      <dt className="text-ink-500 text-xs">{label}</dt>
      <dd className="flex flex-wrap items-baseline gap-1.5">
        <span className="font-display text-ink-900 text-lg font-bold tabular-nums">{value}</span>
        <TrendBadge cur={cur} prev={prev} />
      </dd>
      {note && <dd className="text-ink-400 text-xs">{note}</dd>}
    </div>
  );
}

function StepCard({
  title,
  steps,
  children,
}: {
  title: string;
  steps: FunnelStep[];
  children: React.ReactNode;
}) {
  const max = Math.max(1, ...steps.map((s) => s.visitors));
  return (
    <section className="border-ink-200 rounded-2xl border bg-white p-4">
      <h3 className="font-display text-ink-900 mb-2 text-sm font-bold">{title}</h3>
      <ol className="flex flex-col gap-1">
        {steps.map((s) => (
          <StepRow key={s.key} step={s} max={max} />
        ))}
      </ol>
      <dl className="border-ink-100 mt-3 grid grid-cols-2 gap-3 border-t pt-3">{children}</dl>
    </section>
  );
}

export function FunnelPanel({ stats, rangeLabel }: { stats: FunnelStats; rangeLabel: string }) {
  const entry = stepsOf(stats, 'entry');
  const buy = stepsOf(stats, 'buy');
  const login = stepVisitors(entry, 'login');
  const buyPage = stepVisitors(buy, 'acheter');
  const d = stats.demo;
  return (
    <section aria-label="Parcours d’entrée et d’achat">
      <h2 className="font-display text-ink-900 mb-1 text-lg font-bold">
        Parcours d’entrée et d’achat · {rangeLabel}
      </h2>
      <p className="text-ink-500 mb-3 text-sm">
        Visiteurs uniques de chaque étape, comparés à la période d’avant. Touche une étape pour voir
        ses pages.
      </p>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <StepCard title="Arrivée et inscription" steps={entry}>
          <Result
            label="Démos lancées"
            value={formatMetric('visitors', d.started)}
            cur={d.started}
            prev={d.prev_started}
            note={`${formatMetric('visitors', d.completed)} terminées · ${formatMetric('visitors', d.signup_clicked)} « Créer un compte »`}
          />
          <Result
            label="Comptes créés"
            value={formatMetric('visitors', stats.signups.cur)}
            cur={stats.signups.cur}
            prev={stats.signups.prev}
            note={
              rateLabel(stats.signups.cur, login)
                ? `${rateLabel(stats.signups.cur, login)} des visiteurs de la connexion`
                : null
            }
          />
        </StepCard>
        <StepCard title="Déblocage et achat" steps={buy}>
          <Result
            label="Ventes SKILLCARDS"
            value={formatMetric('revenue_cents', stats.sales.revenue_cents)}
            cur={stats.sales.revenue_cents}
            prev={stats.sales.prev_revenue_cents}
            note={`${plural(stats.sales.cur, 'achat', 'achats')}${
              rateLabel(stats.sales.cur, buyPage)
                ? ` · ${rateLabel(stats.sales.cur, buyPage)} des visiteurs des pages d’achat`
                : ''
            }`}
          />
          <Result
            label="Abonnements créés"
            value={formatMetric('visitors', stats.subscriptions.cur)}
            cur={stats.subscriptions.cur}
            prev={stats.subscriptions.prev}
          />
        </StepCard>
      </div>
    </section>
  );
}
